import { Component, OnInit, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { ApiService } from '../../core/services/api.service';
import { MediaLibraryItem } from '../../core/models/api.models';

@Component({
  selector: 'app-media-library',
  standalone: true,
  imports: [ReactiveFormsModule, FormsModule, DatePipe],
  templateUrl: './media-library.component.html',
  styleUrl: './media-library.component.scss',
})
export class MediaLibraryComponent implements OnInit {
  private readonly api = inject(ApiService);
  private readonly fb = inject(FormBuilder);

  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly error = signal<string | null>(null);
  readonly success = signal<string | null>(null);
  readonly rows = signal<MediaLibraryItem[]>([]);
  readonly page = signal(1);
  readonly total = signal(0);
  readonly limit = 20;
  readonly showUpload = signal(false);
  readonly editingId = signal<number | null>(null);

  search = '';
  selectedFile: File | null = null;

  readonly uploadForm = this.fb.nonNullable.group({
    name: ['', Validators.required],
    description: [''],
  });

  readonly editForm = this.fb.nonNullable.group({
    name: ['', Validators.required],
    description: [''],
  });

  ngOnInit(): void {
    this.load();
  }

  load(page = 1): void {
    this.loading.set(true);
    this.error.set(null);
    this.page.set(page);
    this.api.getMediaImages(page, this.limit, this.search).subscribe({
      next: (data) => {
        this.rows.set(data?.rows || []);
        this.total.set(data?.total || 0);
        this.loading.set(false);
      },
      error: (err: { message?: string }) => {
        this.error.set(err?.message || 'Failed to load media');
        this.loading.set(false);
      },
    });
  }

  searchNow(): void {
    this.load(1);
  }

  openUpload(): void {
    this.showUpload.set(true);
    this.selectedFile = null;
    this.uploadForm.reset({ name: '', description: '' });
    this.success.set(null);
    this.error.set(null);
  }

  cancelUpload(): void {
    this.showUpload.set(false);
    this.selectedFile = null;
  }

  onFilePicked(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] || null;
    this.selectedFile = file;
    if (file && !this.uploadForm.value.name) {
      const base = file.name.replace(/\.[^.]+$/, '');
      this.uploadForm.patchValue({ name: base });
    }
  }

  upload(): void {
    if (this.uploadForm.invalid || !this.selectedFile || this.saving()) {
      this.uploadForm.markAllAsTouched();
      if (!this.selectedFile) this.error.set('Choose an image file');
      return;
    }
    this.saving.set(true);
    this.error.set(null);
    const v = this.uploadForm.getRawValue();
    this.api
      .uploadMediaImage(this.selectedFile, {
        name: v.name.trim(),
        description: v.description.trim() || undefined,
      })
      .subscribe({
        next: () => {
          this.saving.set(false);
          this.showUpload.set(false);
          this.selectedFile = null;
          this.success.set('Image uploaded to S3 and saved in media library');
          this.load(1);
        },
        error: (err: { message?: string }) => {
          this.saving.set(false);
          this.error.set(err?.message || 'Upload failed');
        },
      });
  }

  openEdit(row: MediaLibraryItem): void {
    this.editingId.set(row.id);
    this.editForm.patchValue({
      name: row.name || '',
      description: row.description || '',
    });
  }

  cancelEdit(): void {
    this.editingId.set(null);
  }

  saveEdit(): void {
    const id = this.editingId();
    if (!id || this.editForm.invalid || this.saving()) {
      this.editForm.markAllAsTouched();
      return;
    }
    this.saving.set(true);
    const v = this.editForm.getRawValue();
    this.api
      .updateMediaImage(id, {
        name: v.name.trim(),
        description: v.description.trim() || null,
      })
      .subscribe({
        next: () => {
          this.saving.set(false);
          this.editingId.set(null);
          this.success.set('Media updated');
          this.load(this.page());
        },
        error: (err: { message?: string }) => {
          this.saving.set(false);
          this.error.set(err?.message || 'Update failed');
        },
      });
  }

  remove(row: MediaLibraryItem): void {
    if (!confirm(`Delete media “${row.name}”? (S3 file is not deleted)`)) return;
    this.saving.set(true);
    this.api.deleteMediaImage(row.id).subscribe({
      next: () => {
        this.saving.set(false);
        this.success.set('Media deleted');
        this.load(this.page());
      },
      error: (err: { message?: string }) => {
        this.saving.set(false);
        this.error.set(err?.message || 'Delete failed');
      },
    });
  }

  copyUrl(url: string): void {
    navigator.clipboard?.writeText(url).then(
      () => this.success.set('URL copied'),
      () => this.error.set('Could not copy URL')
    );
  }

  totalPages(): number {
    return Math.max(1, Math.ceil(this.total() / this.limit));
  }
}
